if (!customElements.get('image-product-showcase')) {
  class ImageProductShowcase extends HTMLElement {
    constructor() {
      super();

      this.animations = new Map();
      this.pauseReasons = new Set();
      this.hoveredCards = new Set();
      this.restartTimer = null;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.handleCardEnter = this.handleCardEnter.bind(this);
      this.handleCardLeave = this.handleCardLeave.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.scheduleRestart = this.scheduleRestart.bind(this);
    }

    connectedCallback() {
      this.animationEnabled = this.dataset.animationEnabled === 'true';
      this.speed = Math.max(10, Number(this.dataset.animationSpeed) || 20);
      this.columns = Array.from(this.querySelectorAll('[data-product-showcase-column]'));
      this.cards = Array.from(this.querySelectorAll('[data-product-showcase-card]'));

      this.cards.forEach((card) => {
        card.addEventListener('mouseenter', this.handleCardEnter);
        card.addEventListener('mouseleave', this.handleCardLeave);
      });

      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.addListener(this.handleMotionChange);
      }

      if (!this.animationEnabled || this.motionQuery.matches) return;

      this.pauseReasons.add('offscreen');

      this.intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          this.setPauseReason('offscreen', !entry.isIntersecting);
        },
        { rootMargin: '120px 0px', threshold: 0.05 },
      );
      this.intersectionObserver.observe(this);

      this.resizeObserver = new ResizeObserver(this.scheduleRestart);
      this.resizeObserver.observe(this);

      requestAnimationFrame(() => this.start());
    }

    disconnectedCallback() {
      clearTimeout(this.restartTimer);
      this.stop();

      this.cards.forEach((card) => {
        card.removeEventListener('mouseenter', this.handleCardEnter);
        card.removeEventListener('mouseleave', this.handleCardLeave);
      });

      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);

      if (this.motionQuery.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.handleMotionChange);
      } else {
        this.motionQuery.removeListener(this.handleMotionChange);
      }

      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    start() {
      if (!this.animationEnabled || this.motionQuery.matches || !this.isConnected) return;

      this.stop();

      const readyColumns = this.columns.filter((column) => {
        const track = column.querySelector('[data-product-showcase-track]');
        return track && track.children.length >= 3;
      });

      if (!readyColumns.length) return;

      this.classList.add('image-product-showcase--ready');
      readyColumns.forEach((column) => this.animateColumn(column));
      this.syncAnimationState();
    }

    stop() {
      this.animations.forEach((animation) => animation.cancel());
      this.animations.clear();

      this.columns?.forEach((column) => {
        const track = column.querySelector('[data-product-showcase-track]');
        if (track) track.style.transform = '';
      });

      this.classList.remove('image-product-showcase--ready');
    }

    animateColumn(column) {
      if (!this.isConnected || this.motionQuery.matches) return;

      const track = column.querySelector('[data-product-showcase-track]');
      if (!track || track.children.length < 3) return;

      const direction = column.dataset.direction === 'down' ? 'down' : 'up';
      const computedStyle = window.getComputedStyle(track);
      const gap = Number.parseFloat(computedStyle.rowGap || computedStyle.gap) || 0;
      let movingCard;
      let distance;
      let keyframes;

      if (direction === 'down') {
        movingCard = track.lastElementChild;
        distance = movingCard.getBoundingClientRect().height + gap;
        track.prepend(movingCard);
        track.style.transform = `translate3d(0, -${distance}px, 0)`;
        track.getBoundingClientRect();
        keyframes = [
          { transform: `translate3d(0, -${distance}px, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ];
      } else {
        movingCard = track.firstElementChild;
        distance = movingCard.getBoundingClientRect().height + gap;
        keyframes = [
          { transform: 'translate3d(0, 0, 0)' },
          { transform: `translate3d(0, -${distance}px, 0)` },
        ];
      }

      const duration = Math.max(1800, (distance / this.speed) * 1000);
      const animation = track.animate(keyframes, {
        duration,
        easing: 'linear',
        fill: 'forwards',
      });

      track.style.transform = '';
      this.animations.set(column, animation);

      if (this.pauseReasons.size) animation.pause();

      animation.addEventListener(
        'finish',
        () => {
          this.animations.delete(column);
          animation.cancel();

          if (direction === 'up' && movingCard.isConnected) {
            track.append(movingCard);
          }

          track.style.transform = '';
          requestAnimationFrame(() => this.animateColumn(column));
        },
        { once: true },
      );
    }

    setPauseReason(reason, shouldPause) {
      if (shouldPause) {
        this.pauseReasons.add(reason);
      } else {
        this.pauseReasons.delete(reason);
      }

      this.syncAnimationState();
    }

    syncAnimationState() {
      const shouldPause = this.pauseReasons.size > 0;
      this.classList.toggle('image-product-showcase--paused', shouldPause);

      this.animations.forEach((animation) => {
        if (shouldPause) {
          animation.pause();
        } else {
          animation.play();
        }
      });
    }

    handleCardEnter(event) {
      this.hoveredCards.add(event.currentTarget);
      this.setPauseReason('hover', true);
    }

    handleCardLeave(event) {
      this.hoveredCards.delete(event.currentTarget);
      this.setPauseReason('hover', this.hoveredCards.size > 0);
    }

    handleFocusIn(event) {
      if (event.target.closest('[data-product-showcase-card]')) {
        this.setPauseReason('focus', true);
      }
    }

    handleFocusOut() {
      requestAnimationFrame(() => {
        const focusedCard = this.contains(document.activeElement)
          ? document.activeElement.closest('[data-product-showcase-card]')
          : null;
        this.setPauseReason('focus', Boolean(focusedCard));
      });
    }

    handleVisibilityChange() {
      this.setPauseReason('document-hidden', document.hidden);
    }

    handleMotionChange() {
      if (this.motionQuery.matches) {
        this.stop();
      } else if (this.animationEnabled) {
        this.start();
      }
    }

    scheduleRestart() {
      if (!this.animationEnabled || this.motionQuery.matches) return;

      clearTimeout(this.restartTimer);
      this.restartTimer = setTimeout(() => this.start(), 180);
    }
  }

  customElements.define('image-product-showcase', ImageProductShowcase);
}
